import React, { useEffect, useState, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import WhatsappSelect from "../WhatsappSelect";
import MonetaryInput from "../MonetaryInput";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import DeleteAccountModal from "../DeleteAccountModal";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	dangerZone: {
		marginTop: theme.spacing(2),
	},
	dangerDivider: {
		marginBottom: theme.spacing(1.5),
	},
	dangerTitle: {
		color: theme.palette.error.main,
		fontWeight: 600,
	},
	dangerHint: {
		marginBottom: theme.spacing(1),
	},
	dangerButton: {
		color: theme.palette.error.main,
		borderColor: theme.palette.error.main,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		allTicket: "desabled",
		salesGoal: 0
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [whatsappId, setWhatsappId] = useState("");
	const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);
	const [profileOptions, setProfileOptions] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
	const { loading, whatsApps } = useWhatsApps();

	// Nao-admin edita apenas nome e senha do proprio perfil: e-mail e meta de
	// vendas ficam visiveis, porem desabilitados (o backend tambem ignora esses
	// campos quando quem salva nao e admin).
	const isAdmin = loggedInUser?.profile === "admin";

	// A exclusao so aparece no proprio perfil (nunca quando um admin edita
	// outro usuario). Vale inclusive para quem tem `super`: nesta base o flag
	// esta ligado em varios admins comuns de empresa, e a Apple exige que quem
	// cria conta consiga exclui-la.
	const isOwnProfile =
		!!userId && Number(userId) === Number(loggedInUser?.id);

	useEffect(() => {
        let isSubscribed = true;  // Declara a variável aqui dentro do useEffect

		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);

                // Antes de atualizar o estado, verifica se ainda está "montado"
                if (!isSubscribed) return;

				setUser(prevState => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
				const managedWhatsappIds = data.whatsapps?.map(whatsapp => whatsapp.id) || [];
				// A conexão padrão sempre integra as conexões gerenciadas pelo usuário
				if (data.whatsappId && !managedWhatsappIds.includes(data.whatsappId)) {
					managedWhatsappIds.push(data.whatsappId);
				}
				setSelectedWhatsappIds(managedWhatsappIds);
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();

        // Cleanup do effect: se desmontar, seta isSubscribed = false
        return () => {
            isSubscribed = false;
        };
	}, [userId, open]);

	// Conexão excluída não vem do /whatsapp e não pode continuar selecionada
	// (padrão ou gerenciada): sem isso o id ficava escondido no Select e
	// voltava no submit.
	useEffect(() => {
		if (loading) return;
		const availableIds = new Set(whatsApps.map(w => Number(w.id)));
		setWhatsappId(prev => (prev && !availableIds.has(Number(prev)) ? "" : prev));
		setSelectedWhatsappIds(prev => {
			const next = (prev || []).filter(id => availableIds.has(Number(id)));
			return next.length === (prev || []).length ? prev : next;
		});
	}, [loading, whatsApps, whatsappId, selectedWhatsappIds]);

	useEffect(() => {
		const fetchProfiles = async () => {
			try {
				const { data } = await api.get("/profiles");
				setProfileOptions(data);
			} catch (err) {
				setProfileOptions([]);
			}
		};
		if (open) fetchProfiles();
	}, [open]);

	// "Excluir minha conta" tambem aqui: quem nao tem permissao para ver a aba
	// Usuarios consegue excluir a propria conta pela tela de Perfil.
	const handleOpenDeleteAccount = () => {
		setDeleteAccountOpen(true);
		onClose();
	};

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setWhatsappId("");
		setSelectedWhatsappIds([]);
	};

	const handleSaveUser = async values => {
		// Nao-admin: envia apenas o que ele pode alterar. Mandar o resto faria o
		// backend recusar campos ou o UpdateUserService zerar filas/conexoes.
		const userData = isAdmin
			? { ...values, whatsappId, queueIds: selectedQueueIds, whatsappIds: selectedWhatsappIds, allTicket: values.allTicket }
			: { name: values.name, password: values.password };
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<DeleteAccountModal
				open={deleteAccountOpen}
				onClose={() => setDeleteAccountOpen(false)}
			/>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => {
						const availableProfiles = profileOptions.map((option) => option.perf_name);
						const safeProfileValue = availableProfiles.includes(values.profile)
							? values.profile
							: "";

						return (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.password")}
										type={showPassword ? "text" : "password"}
										name="password"
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										variant="outlined"
										margin="dense"
										fullWidth
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="Mostrar/ocultar senha"
														onClick={() => setShowPassword(prev => !prev)}
														onMouseDown={e => e.preventDefault()}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
										disabled={!isAdmin}
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>
													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-input-label"
														id="profile-selection"
														value={safeProfileValue}
														onChange={(e) => setFieldValue("profile", e.target.value)}
														required
													>
														<MenuItem value={""}>&nbsp;</MenuItem>
														{profileOptions.map((option) => (
															<MenuItem key={option.id} value={option.perf_name}>{option.perf_name}</MenuItem>
														))}
													</Field>
												</>
											)}
										/>
									</FormControl>
								</div>
								<div className={classes.multFieldLine}>
									<Field name="salesGoal">
										{({ field, form }) => (
											<MonetaryInput
												label={i18n.t("userModal.form.salesGoal")}
												name="salesGoal"
												value={field.value}
												onChange={(e) => form.setFieldValue('salesGoal', e.target.value)}
												variant="outlined"
												margin="dense"
												fullWidth
												disabled={!isAdmin}
											/>
										)}
									</Field>
								</div>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
										/>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
											<InputLabel>
												{i18n.t("userModal.form.whatsapp")}
											</InputLabel>
											<Field
												as={Select}
												name="whatsappId"
												id="whatsappId"
												value={whatsappId}
												onChange={(e) => {
													const value = e.target.value;
													setWhatsappId(value);
													// Ao definir a conexão padrão, incluí-la automaticamente nas conexões gerenciadas
													if (value) {
														setSelectedWhatsappIds(prev =>
															prev.includes(value) ? prev : [...prev, value]
														);
													}
												}}
												label={i18n.t("userModal.form.whatsapp")}

											>
												<MenuItem value={''}>&nbsp;</MenuItem>
												{whatsApps.map((whatsapp) => (
													<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
												))}
											</Field>
										</FormControl>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<WhatsappSelect
											selectedWhatsappIds={selectedWhatsappIds}
											onChange={values => setSelectedWhatsappIds(values)}
											whatsApps={whatsApps}
										/>
									)}
								/>
								
								
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (!loading &&
										<div className={classes.textField}>
											<FormControl
												variant="outlined"
												className={classes.maxWidth}
												margin="dense"
												fullWidth
											>
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.allTicket")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.allTicket")}
														name="allTicket"
														labelId="allTicket-selection-label"
														id="allTicket-selection"
														required
													>
														<MenuItem value="enabled">{i18n.t("userModal.form.allTicketEnabled")}</MenuItem>
														<MenuItem value="desabled">{i18n.t("userModal.form.allTicketDesabled")}</MenuItem>
													</Field>
												</>
											</FormControl>
										</div>

									)}
								/>

								{isOwnProfile && (
									<div className={classes.dangerZone}>
										<Divider className={classes.dangerDivider} />
										<Typography
											variant="subtitle2"
											className={classes.dangerTitle}
										>
											{i18n.t("userModal.deleteAccount.title")}
										</Typography>
										<Typography
											variant="body2"
											className={classes.dangerHint}
										>
											{i18n.t("userModal.deleteAccount.hint")}
										</Typography>
										<Button
											onClick={handleOpenDeleteAccount}
											variant="outlined"
											className={classes.dangerButton}
											disabled={isSubmitting}
										>
											{i18n.t("userModal.deleteAccount.button")}
										</Button>
									</div>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
						);
					}}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
